/**
 * PromCard - PROM card primitive (PROD-646 Sprint 3.3).
 *
 * Renders a single PROM (PHQ-9, GAD-7, or MSQ baseline) as an inline
 * Likert-scale form. Submission writes a `QuestionnaireResponse` plus an
 * `Observation` (LOINC-coded) via the FHIR client returned by
 * `getFhirClient()`, then emits an `AuditEvent` with `source=proms-tab`
 * per HC9 strict registry.
 *
 * Render contract:
 *   - Title + brief description + question count.
 *   - Citation line per HC3 (verbatim from the definition).
 *   - "Start" button to expand the inline form. Once expanded, the form
 *     stays open until "Submit" or "Cancel".
 *   - Each question is rendered as a radiogroup of the definition's
 *     Likert options. Required.
 *   - "Submit" calls `getFhirClient().createQuestionnaireResponse(qr,
 *     tenantId)` then `createObservation(obs, tenantId)`. Both throws
 *     surface as a banner on the card.
 *   - On success, the card collapses back to the summary state with the
 *     final score and the verbatim severity-band label visible.
 *
 * HC1 reproducibility: the demo-mode FHIR client is deterministic. The
 *   submission id is built from the active patient id + PROM id +
 *   `nowIso()` so two submissions in the same second produce stable ids.
 *   The component injects `nowIso` so tests can pin a clock.
 *
 * HC2: tokens-only. Tailwind classes resolve through Signal DS tokens.
 *
 * HC3: each card cites the LOINC code + score-interpretation source via
 *   the definition's `citation` field.
 *
 * HC5: zero em / en dashes anywhere.
 *
 * HC6 a11y:
 *   - Section uses `role="article"` + `aria-labelledby`.
 *   - Likert options are native `<input type="radio">` grouped by item.
 *   - Submit is a real `<button>`; disabled state reflects partial fill.
 *   - Result state uses `role="status" aria-live="polite"` so SR users
 *     hear the score the moment it lands.
 *
 * HC9 telemetry-real: every submission emits an AuditEvent (verb
 *   `submitted`, source `proms-tab`) through `auditPayloadToFhir` +
 *   `client.createAuditEvent`. Tests assert against the captured sink.
 *
 * Validation tag: [IMPLEMENTED]
 */
import { useId, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  computePromScore,
  scoreBandFor,
  type PromDefinition,
} from './prom-definitions';
import { getFhirClient } from '../../services/fhir';
import {
  promToFhir,
  auditPayloadToFhir,
  type PromSubmission,
} from '../../services/fhir-mappers';
import { useTenantStore } from '../../state/tenant.store';
import { usePatientStore } from '../../state/patient.store';
import { ValidationTagBadge } from '../ValidationTagBadge';

export interface PromCardProps {
  /** PROM definition driving the card. */
  definition: PromDefinition;
  /**
   * Optional FHIR-style practitioner reference for the submission author
   * (e.g. `Practitioner/dr-aldhaheri`). Optional - the submission still
   * lands without an author.
   */
  practitionerReference?: string;
  /**
   * Optional clock injection for tests. Defaults to `() => new Date().toISOString()`.
   */
  nowIso?: () => string;
  /**
   * Optional callback invoked after a successful submission. Test
   * harnesses use this to assert the QR + Observation pair landed.
   */
  onSubmitted?: (result: PromSubmittedResult) => void;
  className?: string;
}

/** Shape returned to the host on a successful submit. */
export interface PromSubmittedResult {
  promId: PromDefinition['id'];
  submissionId: string;
  score: number;
  bandLabel: string;
  loincCode: string;
}

/** Internal form values type - one entry per question. */
type PromFormValues = Record<string, number>;

const SUBMIT_LABEL = 'Submit';
const CANCEL_LABEL = 'Cancel';
const START_LABEL = 'Start';
const SOURCE_DISCRIMINATOR = 'proms-tab';

function defaultNow(): string {
  return new Date().toISOString();
}

export function PromCard({
  definition,
  practitionerReference,
  nowIso = defaultNow,
  onSubmitted,
  className,
}: PromCardProps) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const activePatientId = usePatientStore((s) => s.activePatientId);
  const titleId = useId();
  const descId = useId();

  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PromSubmittedResult | null>(null);

  // Default each radio group to undefined so the Submit button starts
  // disabled until every item is answered.
  const defaultValues = useMemo<Partial<PromFormValues>>(() => {
    const out: Record<string, number | undefined> = {};
    for (const q of definition.questions) {
      out[q.linkId] = undefined;
    }
    return out;
  }, [definition.questions]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<PromFormValues>({
    defaultValues: defaultValues as PromFormValues,
    mode: 'onChange',
  });

  const watched = watch();
  const allAnswered = definition.questions.every(
    (q) => typeof watched[q.linkId] === 'number',
  );

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      // Build the answers map and compute the score deterministically.
      const answers = new Map<string, number>();
      for (const q of definition.questions) {
        const v = values[q.linkId];
        answers.set(q.linkId, v);
      }
      const score = computePromScore(definition, answers);
      const band = scoreBandFor(definition, score);

      const authored = nowIso();
      const submissionId = `${definition.id}-${activePatientId ?? 'anonymous'}-${authored}`;
      const patientReference = activePatientId
        ? `Patient/${activePatientId}`
        : 'Patient/anonymous';

      const submission: PromSubmission = {
        submissionId,
        questionnaireUrl: definition.questionnaireUrl,
        patientReference,
        practitionerReference,
        tenantId,
        authored,
        items: definition.questions.map((q) => ({
          linkId: q.linkId,
          text: q.text,
          value: answers.get(q.linkId) as number,
        })),
        score: {
          loincCode: definition.loincCode,
          display: definition.loincDisplay,
          value: score,
          unit: definition.scoreUnit,
        },
      };

      const { questionnaireResponse, observation } = promToFhir(submission);
      const client = getFhirClient();
      await client.createQuestionnaireResponse(questionnaireResponse, tenantId);
      await client.createObservation(observation, tenantId);

      // HC9 strict registry: emit AuditEvent with source=proms-tab.
      const auditEvent = auditPayloadToFhir({
        recorded: authored,
        source: SOURCE_DISCRIMINATOR,
        tenantId,
        subject: {
          reference: practitionerReference ?? patientReference,
          typeCode: practitionerReference ? 'practitioner' : 'patient',
        },
        verb: 'submitted',
        verbDisplay: `submitted ${definition.title}`,
        object: {
          reference: `urn:cps:prom:${definition.id}`,
          display: definition.title,
          typeCode: 'prom',
        },
        indirectObject: {
          reference: patientReference,
          typeCode: 'patient',
        },
        action: 'C',
        outcome: '0',
      });
      await client.createAuditEvent(auditEvent, tenantId);

      const resultPayload: PromSubmittedResult = {
        promId: definition.id,
        submissionId,
        score,
        bandLabel: band,
        loincCode: definition.loincCode,
      };
      setResult(resultPayload);
      setExpanded(false);
      onSubmitted?.(resultPayload);
      reset(defaultValues as PromFormValues);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Submission failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <article
      role="article"
      aria-labelledby={titleId}
      data-testid={`prom-card-${definition.id}`}
      data-prom-id={definition.id}
      data-loinc-code={definition.loincCode}
      className={[
        'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex flex-col gap-gutter-sm">
        <div className="flex items-center justify-between gap-gutter-sm">
          <h3
            id={titleId}
            className="text-signal-lg font-semibold text-text-primary"
          >
            {definition.title}
          </h3>
          <ValidationTagBadge tag="[IMPLEMENTED]" anchorId={definition.id} />
        </div>
        <p
          id={descId}
          className="text-signal-sm text-text-secondary"
        >
          {definition.description}
        </p>
        <p
          data-testid={`prom-card-${definition.id}-question-count`}
          className="text-signal-xs text-text-tertiary"
        >
          {definition.questions.length} questions
        </p>
        <p
          data-testid={`prom-card-${definition.id}-citation`}
          className="text-signal-xs text-text-tertiary"
        >
          {definition.citation}
        </p>
        {definition.advisoryFlag ? (
          <p
            role="note"
            data-testid={`prom-card-${definition.id}-advisory`}
            className="rounded-signal-sm bg-warning-soft-bg px-gutter-sm py-1 text-signal-xs text-warning-soft-fg"
          >
            {definition.advisoryFlag}
          </p>
        ) : null}
      </header>

      {result ? (
        <section
          role="status"
          aria-live="polite"
          data-testid={`prom-card-${definition.id}-result`}
          className="flex flex-col gap-1 rounded-signal-md border border-surface-border bg-surface-muted px-gutter-md py-gutter-sm"
        >
          <p className="text-signal-xs uppercase tracking-wide text-text-tertiary">
            Last score
          </p>
          <p className="text-signal-xl font-semibold text-text-primary">
            {result.score}
          </p>
          <p className="text-signal-sm text-text-secondary">
            {result.bandLabel}
          </p>
          <p className="text-signal-xs text-text-tertiary">
            LOINC {result.loincCode}
          </p>
        </section>
      ) : null}

      {!expanded ? (
        <div className="flex items-center justify-between gap-gutter-md">
          <p className="text-signal-xs text-text-tertiary">
            Tenant: {tenantId}
          </p>
          <button
            type="button"
            data-testid={`prom-card-${definition.id}-start`}
            onClick={() => {
              setExpanded(true);
              setError(null);
            }}
            className="rounded-signal-md bg-signal-primary-600 px-gutter-md py-1 text-signal-sm font-medium text-text-inverse hover:bg-signal-primary-700"
          >
            {START_LABEL}
          </button>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          data-testid={`prom-card-${definition.id}-form`}
          className="flex flex-col gap-gutter-md"
        >
          <ol className="flex flex-col gap-gutter-md">
            {definition.questions.map((q, idx) => {
              const groupId = `${definition.id}-${q.linkId}`;
              return (
                <li
                  key={q.linkId}
                  className="flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-muted px-gutter-md py-gutter-sm"
                >
                  <p className="text-signal-sm font-medium text-text-primary">
                    {idx + 1}. {q.text}
                  </p>
                  <Controller
                    name={q.linkId}
                    control={control}
                    rules={{
                      validate: (v) =>
                        typeof v === 'number' || 'Required',
                    }}
                    render={({ field }) => (
                      <div
                        role="radiogroup"
                        aria-label={q.text}
                        data-testid={`prom-card-${definition.id}-group-${q.linkId}`}
                        className="flex flex-wrap gap-gutter-sm"
                      >
                        {q.options.map((opt) => {
                          const inputId = `${groupId}-${opt.value}`;
                          const isSelected = field.value === opt.value;
                          return (
                            <label
                              key={opt.value}
                              htmlFor={inputId}
                              data-testid={`prom-card-${definition.id}-${q.linkId}-${opt.value}`}
                              className={[
                                'flex cursor-pointer items-center gap-1 rounded-signal-pill border px-gutter-sm py-1 text-signal-xs',
                                isSelected
                                  ? 'border-signal-primary-600 bg-signal-primary-100 text-signal-primary-700'
                                  : 'border-surface-border bg-surface-base text-text-secondary',
                              ].join(' ')}
                            >
                              <input
                                id={inputId}
                                type="radio"
                                name={groupId}
                                value={opt.value}
                                checked={isSelected}
                                onChange={() => field.onChange(opt.value)}
                                className="h-3 w-3 accent-signal-primary-600"
                              />
                              <span>
                                {opt.label} ({opt.value})
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  />
                </li>
              );
            })}
          </ol>

          {error ? (
            <p
              role="alert"
              data-testid={`prom-card-${definition.id}-error`}
              className="text-signal-sm text-warning-strong-fg"
            >
              {error}
            </p>
          ) : null}

          <div className="flex items-center justify-end gap-gutter-sm">
            <button
              type="button"
              data-testid={`prom-card-${definition.id}-cancel`}
              onClick={() => {
                setExpanded(false);
                setError(null);
                reset(defaultValues as PromFormValues);
              }}
              className="rounded-signal-md border border-surface-border bg-surface-base px-gutter-md py-1 text-signal-sm font-medium text-text-secondary hover:bg-surface-muted"
            >
              {CANCEL_LABEL}
            </button>
            <button
              type="submit"
              data-testid={`prom-card-${definition.id}-submit`}
              disabled={!allAnswered || submitting || !isValid}
              className="rounded-signal-md bg-signal-primary-600 px-gutter-md py-1 text-signal-sm font-medium text-text-inverse hover:bg-signal-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting' : SUBMIT_LABEL}
            </button>
          </div>
        </form>
      )}
    </article>
  );
}
